<template>
    <v-dialog v-model="dialog" max-width="800" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ editMode ? $t('common.edit') : $t('common.create') }} {{ $t(`common.template`) }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-row>
                    <v-col cols="12" md="6">
                        <kimsa-text-field outlined dense force-value :placeholder="$t('common.template_name')"
                            @change="name = $event" :value="name" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-alert color="pink" dark border="left" dense dismissible>
                            <small>{{ $t('common.update_condition') }}</small>
                        </v-alert>
                    </v-col>
                    <v-col cols="12">
                        <!-- HEADERS -->
                        <v-row>
                            <v-col cols="3" class="pb-0">
                                {{ $t('common.evaluation_name') }}
                            </v-col>
                            <v-col cols="3" class="pb-0">
                                {{ $t('common.unit_measure') }}
                            </v-col>
                            <v-col cols="3" class="pb-0">
                                {{ $t('common.ideal_value') }}
                            </v-col>
                            <v-col cols="2" class="pb-0">
                                {{ $t('common.upgrade') }}
                            </v-col>
                            <v-col cols="1" class="pb-0" />
                        </v-row>
                        <!-- CONFIG -->
                        <v-row v-for="(item, i) in config" :key="i">
                            <v-col cols="3">
                                <kimsa-text-field outlined dense force-value :placeholder="$t('common.name')"
                                    @change="config[i].name = $event" :value="config[i].name" />
                            </v-col>
                            <v-col cols="3">
                                <kimsa-combobox outlined dense :items="units" @change="config[i].unit = $event"
                                    :value="config[i].unit" no-object />
                            </v-col>
                            <v-col cols="3">
                                <kimsa-text-field outlined dense force-value :placeholder="$t('common.ideal_value')"
                                    @change="config[i].value = $event" :value="config[i].value" type="number"
                                    input-mode="numeric" />
                            </v-col>
                            <v-col cols="2" align="center">
                                <v-btn fab x-small depressed dark class="mt-1 mr-1" @click="config[i].upgrade = true"
                                    :color="config[i].upgrade === true ? 'secondary' : 'grey'">
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                                <v-btn fab x-small depressed dark class="mt-1 mr-1" @click="config[i].upgrade = false"
                                    :color="config[i].upgrade === false ? 'secondary' : 'grey'">
                                    <v-icon>mdi-minus</v-icon>
                                </v-btn>
                            </v-col>
                            <v-col cols="1" align="center">
                                <v-btn v-if="i !== 0" fab x-small depressed dark class="mt-1 mr-1" @click="deleteRow(i)"
                                    color="pink">
                                    <v-icon>$delete</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-btn depressed dark color="secondary" class="normal-btn" @click="addNewRow">
                                    {{ $t('common.add_new_evaluation') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text"
                    :disabled="disabled" @click="save" :loading="loading">
                    {{ editMode ? $t('common.edit') : $t('common.create') }}
                </v-btn>
            </v-card-actions>
        </v-card>

    </v-dialog>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import { mapMutations, mapState } from "vuex";
import { db, FieldValue } from "@/utils/Firebase";
import { evaluationUnits } from "@/utils/LocalData";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { randomString } from "@/utils/Common";

export default {
    name: "CreateEvaluationDialog",
    components: { KimsaCombobox, KimsaTextField },
    props: {
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            editMode: false,
            units: evaluationUnits,
            configTemplate: { name: '', unit: 'none', value: 0, upgrade: true },
            name: '',
            config: [{ id: randomString(12), name: '', unit: 'none', value: 0, upgrade: true }],
            evaluation: null,
        }
    },
    computed: {
        ...mapState(['dialogActive', 'secondDialogActive']),
        disabled(vm = this) {
            if (vm.name.length < 3) return true
            return !vm.config?.every(c => c.name)
        }
    },
    methods: {
        ...mapMutations(['setDialogActive', 'setSecondDialogActive']),
        show() {
            const vm = this
            vm.dialog = true
            vm.isSecondDialog ? vm.setSecondDialogActive(true) : vm.setDialogActive(true)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.isSecondDialog ? vm.setSecondDialogActive(false) : vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.evaluation = null
            vm.name = ''
            vm.config = [{ ...vm.configTemplate, id: randomString(12) }]
            vm.editMode = false
        },
        loadEvaluation(evaluation) {
            const vm = this
            vm.editMode = true
            vm.evaluation = evaluation
            vm.name = evaluation.name
            vm.config = evaluation.config
            vm.show()
        },
        addNewRow() {
            const vm = this
            vm.config.push({ ...vm.configTemplate, id: randomString(12) })
        },
        deleteRow(i) {
            const vm = this
            vm.config.splice(i, 1)
        },
        async save() {
            const vm = this
            vm.loading = true

            if (vm.editMode) {
                console.log('vm.evaluation', vm.evaluation)
                await db.collection('evaluations').doc(vm.evaluation.id).update({
                    name: vm.name,
                    config: vm.config
                })
                vm.toast(vm.$t('common.template_updated'))
            } else {
                await db.collection('evaluations').add({
                    coachId: vm.currentUser.uid,
                    name: vm.name,
                    config: vm.config,
                })
                vm.toast(vm.$t('common.template_created'))
            }

            vm.loading = false
            vm.hide()
        }
    },
    watch: {
        dialogActive(val) {
            if (!val && this.dialog) this.hide()
        },
        secondDialogActive(val) {
            if (!val && this.dialog) this.hide()
        },
    }
}
</script>

<style scoped>
.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}
</style>